import type { ComponentProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ProductEventDataList } from 'corgi-types';
import { size } from 'lodash-es';
import {
  backgroundColor,
  display,
  flexDirection,
  height,
  position,
  space,
  themeGet,
} from 'styled-system';

import type { ProductSuggestionItem } from '../../../global-header/components/toolbar/types';
import { focusOutlineCSSWithOffsetAndRadius, textWithEllipsis } from '../../theme/styles';
import { cursor, textDecoration, themeRight, themeTop } from '../../theme/system-utilities';
import { dispatchTrackingEventGA4 } from '../../tracking/dispatchTrackingEventGA4';
import ArticleSmallPrice, { RenderingStrategy } from '../Price/ArticleSmallPrice';
import ResponsiveProductImage from '../ProductCard/components/ResponsiveProductImage';
import { SubtitleST4 } from '../Typography';
import WishlistButtonV2 from '../WishlistButton/WishlistButtonV2';
import type { ProductSuggestionItemProps, SuggestionGetItemProps } from './types';
import { getProductSuggestionTrackingData } from './utils/tracking';

const IMAGE_SIZES = [301, 301, 164, 164];

const UnstyledContainer = styled.div<any>`
  ${backgroundColor};
  ${cursor};
  ${display};
  ${flexDirection};
  ${position};
  ${height};

  ${props =>
    props.highlighted &&
    css`
      outline: ${themeGet('borderWidths.small')(props)} solid
        ${themeGet('colors.sys.neutral.border.hover')(props)};
      border-radius: ${themeGet('radii.medium')(props)};
    `};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer
    bg="sys.neutral.background.default"
    cursor="pointer"
    display="flex"
    flexDirection="column"
    position="relative"
    height="100%"
    {...props}
  />
);

const UnstyledLinkContainer = styled.a<any>`
  ${textDecoration};
  ${position};
  ${space};
  ${height};

  ${focusOutlineCSSWithOffsetAndRadius};
`;

const LinkContainer = (props: ComponentProps<typeof UnstyledLinkContainer>) => (
  <UnstyledLinkContainer
    textDecoration="none"
    position="relative"
    p="sp_8"
    height="100%"
    {...props}
  />
);

const ProductName = styled(SubtitleST4)`
  ${textWithEllipsis(2)};
`;

const UnstyledStyledWishlistButton = styled(WishlistButtonV2)<any>`
  ${position};
  ${themeRight};
  ${themeTop};
  ${size};
`;

const StyledWishlistButton = (props: ComponentProps<typeof UnstyledStyledWishlistButton>) => (
  <UnstyledStyledWishlistButton
    position="absolute"
    themeRight="sp_12"
    themeTop="sp_12"
    p="sp_8"
    {...props}
  />
);

export type SuggestionProductCardProps = ProductSuggestionItem & {
  highlightedIndex: number;
  productsStartIndex: number;
  getItemProps: SuggestionGetItemProps<ProductSuggestionItemProps>;
};

const SuggestionProductCard = ({
  sku,
  name,
  productImage,
  url,
  prices: { regular, special, rrp, measurement },
  index,
  metrics,
  highlightedIndex,
  productsStartIndex,
  getItemProps,
}: SuggestionProductCardProps) => {
  const trackingData = getProductSuggestionTrackingData({
    index,
    tracking: {
      productName: name,
      productSimpleSKU: sku,
      productPrice: special?.value ?? regular?.value,
    },
    list: ProductEventDataList.SearchAutoSuggest,
  });

  const mainIndex = index + productsStartIndex;

  const handleLinkClick = (event: MouseEvent) => {
    // We want to preserve link behavior
    // and not delegate it to Downshift,
    // that way people can click on a link
    // to open in new tabs, etc
    event.stopPropagation();
    dispatchTrackingEventGA4(trackingData);
  };

  return (
    <Container
      data-testid="suggestions-product-item"
      data-sku={sku}
      {...getItemProps({
        item: { url, trackingData },
        index: mainIndex,
        highlighted: highlightedIndex === mainIndex,
      })}
    >
      <LinkContainer href={url} onClick={handleLinkClick}>
        <ResponsiveProductImage
          key={productImage}
          images={[{ path: productImage }]}
          widths={IMAGE_SIZES}
          fallbackSize={IMAGE_SIZES[1]}
          alt={name}
          isReplaced
          visibleProductsCount={0}
        />
        {regular && (
          <ArticleSmallPrice
            regularPrice={regular}
            rrpPrice={rrp}
            specialPrice={special}
            measurement={measurement}
            renderingStrategy={RenderingStrategy.Horizontal}
          />
        )}
        <ProductName>{name}</ProductName>
      </LinkContainer>
      <StyledWishlistButton
        hideCount // hides the count until https://home24.atlassian.net/browse/SPP-5041 is done
        sku={sku}
        size={16}
        count={metrics?.wishlist.count}
        index={index}
        tracking={{
          productSimpleSKU: sku,
          productName: name,
          productPrice: special?.value ?? regular?.value,
          list: ProductEventDataList.SearchAutoSuggest,
        }}
        list={ProductEventDataList.SearchAutoSuggest}
      />
    </Container>
  );
};

export default SuggestionProductCard;
